import { useEffect, useId, useRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'label'> {
  label?: ReactNode;
  description?: ReactNode;
  indeterminate?: boolean;
}

export function Checkbox({
  id,
  label,
  description,
  indeterminate = false,
  className = '',
  ...props
}: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className={`ui-checkbox${className ? ` ${className}` : ''}`}>
      <input ref={inputRef} id={inputId} type="checkbox" className="ui-checkboxInput" {...props} />
      <label htmlFor={inputId} className="ui-checkboxLabel">
        <span className="ui-checkboxBox" aria-hidden="true">
          <svg
            className="ui-checkboxIcon ui-checkboxIcon--check"
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            className="ui-checkboxIcon ui-checkboxIcon--dash"
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
        {(label || description) && (
          <span className="ui-checkboxText">
            {label && <span className="ui-checkboxTitle">{label}</span>}
            {description && <span className="ui-checkboxDescription">{description}</span>}
          </span>
        )}
      </label>
    </div>
  );
}
